import { Link, useParams } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useAsync } from "../../app/useAsync";
import {
	Badge,
	Callout,
	Card,
	DefinitionList,
	LinkButton,
	PageHeader,
	Table,
} from "../../ui/primitives";
import { EmptyState, ErrorState, LoadingState, NotFoundState } from "../../ui/states";
import { findVertical, LEGAL_PAGES, VERTICALS } from "./content";

const PUBLIC_SCOPE = { tenantId: "tenant-01", projectId: "project-01" };

function PageFrame({ children }: { children: React.ReactNode }) {
	return <div className="mx-auto max-w-6xl px-4 py-10 lg:px-8">{children}</div>;
}

// ---------------------------------------------------------------------------
// Vertical / UCS LP
// ---------------------------------------------------------------------------

export function SolutionsIndexPage() {
	return (
		<PageFrame>
			<PageHeader
				eyebrow="業種別ソリューション"
				title="業種ごとに、担当できる業務が違います"
				description="Agentryの差別化はLLMそのものではなく、業種ごとのKnowledge・Workflow・Quality Gate・KPIをパッケージで持っていることです。"
			/>
			<div className="grid gap-4 lg:grid-cols-2">
				{VERTICALS.map((vertical) => (
					<Card
						key={vertical.key}
						title={
							<span className="flex items-center gap-2">
								{vertical.name}
								<Badge tone={vertical.priority === "重点" ? "brand" : "neutral"}>
									{vertical.priority}
								</Badge>
							</span>
						}
						description={vertical.tagline}
						action={
							<LinkButton to={`/solutions/${vertical.key}`} variant="secondary" size="sm">
								詳しく見る
							</LinkButton>
						}
					>
						<DefinitionList
							columns={1}
							items={[
								{ label: "提供する業務成果", value: vertical.outcome },
								{ label: "主なKPI", value: vertical.kpis.join(" / ") },
							]}
						/>
					</Card>
				))}
			</div>
		</PageFrame>
	);
}

export function SolutionDetailPage() {
	const { verticalKey = "" } = useParams();
	const vertical = findVertical(verticalKey);
	if (!vertical) {
		return (
			<PageFrame>
				<NotFoundState detail="指定された業種のページは公開されていません。" />
			</PageFrame>
		);
	}

	return (
		<PageFrame>
			<PageHeader
				eyebrow={`${vertical.priority}Vertical`}
				title={`${vertical.name}向け Agentry`}
				description={vertical.tagline}
				actions={
					<>
						<LinkButton to="/signup">この業種で始める</LinkButton>
						<LinkButton to="/demo?api=mock" variant="secondary">
							デモを見る
						</LinkButton>
					</>
				}
			/>

			<Callout tone="brand" title={`提供範囲：${vertical.scope}`}>
				制作物を納品して終わりではなく、{vertical.outcome}までを担当範囲としています。
			</Callout>

			<div className="mt-6 grid gap-4 lg:grid-cols-2">
				<Card title="Agentが持っている業種の常識">
					<ul className="ml-4 list-disc space-y-1.5 text-[14px] text-ink-700">
						{vertical.knowledge.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
				</Card>
				<Card title="公開前に必ず通す品質チェック" description="下位設定から弱めることはできません">
					<ul className="ml-4 list-disc space-y-1.5 text-[14px] text-ink-700">
						{vertical.gates.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ul>
				</Card>
				<Card title="成果の測り方">
					<div className="flex flex-wrap gap-1.5">
						{vertical.kpis.map((kpi) => (
							<Badge key={kpi} tone="neutral">
								{kpi}
							</Badge>
						))}
					</div>
				</Card>
				<Card title="対応しているUCS">
					{vertical.ucsIds.length === 0 ? (
						<p className="text-[14px] text-ink-600">
							このVerticalのUCSは設計済みで、POCでは未公開です。公開時期はお問い合わせください。
						</p>
					) : (
						<ul className="space-y-1.5">
							{vertical.ucsIds.map((ucsId) => (
								<li key={ucsId}>
									<Link
										to={`/agents/${ucsId}`}
										className="text-[14px] font-semibold text-brand-600 hover:underline"
									>
										{ucsId}
									</Link>
								</li>
							))}
						</ul>
					)}
				</Card>
			</div>
		</PageFrame>
	);
}

// ---------------------------------------------------------------------------
// Agent catalog（公開）
// ---------------------------------------------------------------------------

export function PublicAgentsPage() {
	const { client } = useApi();
	const catalog = useAsync(() => client.getCatalog(PUBLIC_SCOPE), [client]);

	return (
		<PageFrame>
			<PageHeader
				eyebrow="Agent紹介"
				title="どのAgentに頼めますか"
				description="Agentは Common（共通の進め方）+ Vertical（業種の常識）+ Custom（自社ルール）で構成されます。"
			/>
			{catalog.status === "loading" && <LoadingState />}
			{catalog.status === "error" && <ErrorState error={catalog.error} onRetry={catalog.reload} />}
			{catalog.status === "ready" && (
				<div className="grid gap-4 lg:grid-cols-2">
					{catalog.data.ucs
						.filter((ucs) => ucs.status === "PUBLISHED")
						.map((ucs) => {
							const agent = catalog.data.agents.find((a) => a.id === ucs.agentId);
							return (
								<Card
									key={ucs.id}
									title={ucs.displayName}
									description={ucs.description}
									action={
										<LinkButton to={`/agents/${ucs.id}`} variant="secondary" size="sm">
											詳細
										</LinkButton>
									}
								>
									<div className="flex flex-wrap items-center gap-1.5">
										<Badge tone="brand">{ucs.categoryCode}</Badge>
										{agent && <Badge tone="neutral">{agent.layer}</Badge>}
										{ucs.verticalKey && <Badge tone="success">{ucs.verticalKey}</Badge>}
										<span className="font-mono text-[11px] text-ink-400">{ucs.id}</span>
									</div>
								</Card>
							);
						})}
				</div>
			)}
		</PageFrame>
	);
}

export function PublicAgentDetailPage() {
	const { ucsId = "" } = useParams();
	const { client } = useApi();
	const catalog = useAsync(() => client.getCatalog(PUBLIC_SCOPE), [client]);

	if (catalog.status === "loading")
		return (
			<PageFrame>
				<LoadingState />
			</PageFrame>
		);
	if (catalog.status === "error")
		return (
			<PageFrame>
				<ErrorState error={catalog.error} onRetry={catalog.reload} />
			</PageFrame>
		);

	const ucs = catalog.data.ucs.find((u) => u.id === ucsId);
	if (ucs?.status !== "PUBLISHED") {
		return (
			<PageFrame>
				<NotFoundState detail="このAgentは公開されていません。" />
			</PageFrame>
		);
	}
	const agent = catalog.data.agents.find((a) => a.id === ucs.agentId);
	const pack = catalog.data.verticalPackages.find((v) => v.key === ucs.verticalKey);
	const workflow = catalog.data.workflowDefinitions.find(
		(w) => w.key === ucs.workflowDefinitionKey,
	);

	return (
		<PageFrame>
			<PageHeader
				eyebrow={ucs.id}
				title={ucs.displayName}
				description={ucs.description}
				actions={<LinkButton to="/signup">このAgentに依頼する</LinkButton>}
			/>

			<div className="grid gap-4 lg:grid-cols-2">
				<Card title="担当Agent">
					{agent ? (
						<DefinitionList
							columns={1}
							items={[
								{ label: "Agent", value: agent.displayName },
								{ label: "構成", value: `${agent.layer} レイヤー` },
								{ label: "説明", value: agent.description },
							]}
						/>
					) : (
						<EmptyState title="Agent情報がありません" />
					)}
				</Card>

				<Card title="進め方" description="公開済みWorkflowに沿って進みます">
					{workflow ? (
						<ol className="space-y-1.5">
							{workflow.nodes.map((node, index) => (
								<li key={node.nodeId} className="flex items-start gap-2 text-[14px] text-ink-700">
									<span className="mt-0.5 w-5 shrink-0 text-[12px] font-bold text-ink-400">
										{index + 1}
									</span>
									<span>
										{node.displayName}
										{node.nodeType === "QUALITY_GATE" && <Badge tone="warning">品質チェック</Badge>}
										{node.nodeType === "APPROVAL" && <Badge tone="brand">あなたの承認</Badge>}
									</span>
								</li>
							))}
						</ol>
					) : (
						<EmptyState title="Workflowが未公開です" />
					)}
				</Card>

				{pack && (
					<>
						<Card title="最初に聞かれること" description="この質問に答えるだけで進みます">
							<ol className="ml-4 list-decimal space-y-1.5 text-[14px] text-ink-700">
								{pack.guidedQuestions.map((q) => (
									<li key={q.key}>{q.prompt}</li>
								))}
							</ol>
						</Card>
						<Card title="公開前の品質チェック">
							<Table head={["チェック", "種類", "判定方法"]}>
								{pack.qualityGates.map((gate) => (
									<tr key={gate.key}>
										<td>{gate.displayName}</td>
										<td>
											<Badge tone={gate.gateType === "POLICY" ? "danger" : "warning"}>
												{gate.gateType}
											</Badge>
										</td>
										<td className="text-ink-600">{gate.evaluator}</td>
									</tr>
								))}
							</Table>
						</Card>
					</>
				)}
			</div>
		</PageFrame>
	);
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

export function PricingPage() {
	const { client } = useApi();
	const plans = useAsync(async () => {
		// Plan一覧は公開情報。System Admin権限が無い状態でも参照できるよう静的定義を使う。
		return [
			{ id: "plan-trial", name: "Trial", price: 0, hours: 30, seats: 3, note: "まず試す" },
			{
				id: "plan-basic",
				name: "Basic",
				price: 49800,
				hours: 80,
				seats: 10,
				note: "1業種で運用する",
			},
			{
				id: "plan-pro",
				name: "Pro",
				price: 148000,
				hours: 160,
				seats: 30,
				note: "複数案件を並行する",
			},
			{
				id: "plan-enterprise",
				name: "Enterprise",
				price: null,
				hours: 720,
				seats: 300,
				note: "全社導入",
			},
		];
	}, [client]);

	return (
		<PageFrame>
			<PageHeader
				eyebrow="料金"
				title="使った分だけ、見えるかたちで"
				description="実行ごとに費用が記録されます。実費・使用量・取得できない場合を区別して表示します。"
			/>
			{plans.status === "loading" && <LoadingState />}
			{plans.status === "error" && <ErrorState error={plans.error} onRetry={plans.reload} />}
			{plans.status === "ready" && (
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{plans.data.map((plan) => (
						<Card key={plan.id} title={plan.name} description={plan.note}>
							<p className="text-[26px] font-bold tracking-tight text-ink-900">
								{plan.price === null
									? "個別見積"
									: plan.price === 0
										? "¥0"
										: `¥${plan.price.toLocaleString()}`}
								{plan.price ? (
									<span className="text-[13px] font-normal text-ink-500"> /月</span>
								) : null}
							</p>
							<ul className="mt-3 space-y-1 text-[13.5px] text-ink-600">
								<li>実行時間 {plan.hours}h / 月</li>
								<li>{plan.seats} シート</li>
							</ul>
							<LinkButton
								to={plan.price === null ? "/contact" : "/signup"}
								variant={plan.id === "plan-pro" ? "primary" : "secondary"}
								size="sm"
								className="mt-4 w-full"
							>
								{plan.price === null ? "相談する" : "始める"}
							</LinkButton>
						</Card>
					))}
				</div>
			)}
			<Callout tone="neutral" title="費用の考え方">
				LLM API・実行環境・ストレージ・外部APIなど、発生源ごとに費用を記録します。
				Subscription経由で金額を正確に取得できない場合は、推定と実費を混ぜずに「取得不能」として表示します。
			</Callout>
		</PageFrame>
	);
}

// ---------------------------------------------------------------------------
// Legal / Contact
// ---------------------------------------------------------------------------

export function LegalPage() {
	const { pageId = "" } = useParams();
	const page = LEGAL_PAGES[pageId];
	if (!page) {
		return (
			<PageFrame>
				<NotFoundState detail="このページは存在しません。" />
			</PageFrame>
		);
	}
	return (
		<div className="mx-auto max-w-3xl px-4 py-10 lg:px-8">
			<PageHeader eyebrow="Legal" title={page.title} />
			<Callout tone="warning" title="POCサンプル">
				本文書はPOC用のサンプルであり、実際の契約条件ではありません。
			</Callout>
			<div className="mt-6 space-y-6">
				{page.sections.map((section) => (
					<section key={section.heading}>
						<h2 className="text-[16px] font-bold text-ink-900">{section.heading}</h2>
						<p className="mt-1.5 text-[14.5px] leading-relaxed text-ink-700">{section.body}</p>
					</section>
				))}
			</div>
		</div>
	);
}
